import {
    FeatureBuyTimesBetValue
} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetFeatureController";
import { DisplayObject } from "pixi.js";

export interface IData {
    mode: string;
    nextMode: string;
    reels: string[][];
    accumulatedRoundWin: number;
    scatterCount: number;
    maxExpansion: boolean;
    multiplier: number;
    monsters: IMonster[];
    freespinsLeft: number;
    mayhemSpin: boolean;
    carriedSymbolUserNode: IPosition;
    brokeTheBank: boolean;
    boostedBet: boolean;
    reelsBeforeAvalanche: string[][];
    reelsAfterAvalanche: string[][];
    lifeLeftInitial: number;
    mxLeftBottomCorner: IMonsterWalk;
}

export interface IInitData extends IData {
    isRestoreState: boolean;
    nextPlayerInteraction: any;
    featureBuyTimesBetValue: FeatureBuyTimesBetValue[],
    wasFeatureBuy: boolean;
    symbolValues: ISymbolValues;
    symbolSizesAfterAvalanche: number[][];
}

export interface IGameData extends IData {
    win: number;
    spinWin: number;
    freespinsPlayed: number;
    playedBetValue: number;
    verticalXSplit: boolean;
    evaluatedArea: string[][];
    bulldozerPath: IMonsterWalk[];
    grenadePosition: IMonsterWalk;
    bazookaPosition: IMonsterWalk;
    airStrikeRocketCoordinates: IMonsterWalk[];
    expansionDirections: number[][];
    gridExpanderDirections: number[][];
    suicideBombs: IMonsterWalk[];
    wasFeatureBuy: boolean;
    mxLeftBottomCornerAtStart: IMonsterWalk;
    extraSpinCount: number;
    suspendedAction: boolean;
}

interface ICollectWins {
    multipliedAmount: number;
}

export interface IPosition {
    x: number;// this is reel id
    y: number; // this is row id
}

export interface IMonsterWalk extends IPosition {
    canRemove: boolean; // used for Wilds as they are shared among Monsters, so the last monster should remove it
}

export interface IMonster {
    carriedSymbols: string[];
    collectWins: ICollectWins[];
    lifeLeft: number;
    path: IMonsterWalk[];
    symbol: number;
    collectedScatterCount: number;
    isSingleMover: boolean;
    lastMultiplierPosition: IPosition;
}

export interface IFeatureData {
    reelId: number;
    rowId: number;
}

export interface IXNudgeData extends IFeatureData {
    nudgeDownwardsRowId: number;
}

export interface IMonsterNameToIdx {
    [name: string]: number;
}

export interface IMapStrToStr {
    [name: string]: string;
}

export interface IHandleCorner {
    leftCRReelId: number;
    leftCRRowId: number;
    rightCRReelId: number;
    rightCRRowId: number;
}

export interface IBigWinLevel {
    from: number;
    to: number;
    duration: number;
    id: number;
    name: string;
}

interface IDataMap {
    [name: string]: string;
}

export interface ISymbolValues {
    [symbolName: string]: { [numSymbols: string]: number };
}

export interface IBigWinLevel {
    from: number;
    to: number;
    duration: number;
    id: number;
    name: string;
}

export interface INormalWinConfig {
    duration: number;
    name: string;
}
export interface IShakeConfig {
    x: number;
    y: number;
    obj: DisplayObject;
    duration: number;
    intensity?: number;
    hertz: number;
}

export enum NORMAL_WIN_LEVEL_NAMES {
    TINY = "TINY",
    SMALL = "SMALL",
    MEDIUM = "MEDIUM",
    LARGE = "LARGE"
}

export const WILD_SKIN_NAMES: IDataMap = {
    "C1": "colourA",
    "C2": "colourB",
    "C3": "colourC",
    "C4": "colourD"
};

export const SCATTER_METER_MAP: IDataMap = {
    "0": "base",
    "1": "collect1",
    "2": "collect2",
    "3A": "anticipation3",
    "3": "collect3",
    "4A": "anticipation4",
    "4": "collect4",
    "FS": "winFS",
    "SFS": "winSFS",
    "CARNAGE_ROYAL_LOOP": "carnage_royal_loop",
    "CARNAGE_SPINS_LOOP": "carnage_spins_loop"
}

export enum BIG_WIN_LEVEL_NAMES {
    BIG_WIN_01 = "BIG_WIN_01",
    BIG_WIN_02 = "BIG_WIN_02",
    BIG_WIN_03 = "BIG_WIN_03",
    BIG_WIN_04 = "BIG_WIN_04",
    BIG_WIN_05 = "BIG_WIN_05"
}

export enum REEL_CORNER_NAMES {
    'A',
    'B',
    'C',
    'D'
}

export const MONSTER_MOVE_ACTIONS = {
    IDLE: "idle",
    MOVING: "moving",
    MOVING_FAST: "moving_fast",
    PICKUP_MULTIPLIER: "pickup_multiplier",
    TURN: "turn",
    ATTRACT_SYMBOLS: "attract_symbols"
}

export const MONSTER_WEAPON_ACTIONS = {
    AS_PICK: "as/pickup_as",
    BU_PICK: "bu/pickup_bu",
    BZ_PICK: "bz/pickup_bz",
    GR_PICK: "gr/pickup_gr",
    S_PICKUP: "s/pickup_s",
    AS_HIT: "airstrike_hit",
    S_USE: "s/use_s",
    BU_LOOP: "bu/bu_loop",
    BU_END: "bu/bu_end",
    SUICIDE: "suicide",
    SUICIDE_BIG: "suicide_big",
    OUT_OF_LIVES: "out_of_lives",
    SUICIDE_OOL: "suicide_tombstone",
    SUICIDE_BIG_OOL: "suicide_big_tombstone",
    IDLE1: "idle_1",
    IDLE2: "idle_2",
    IDLE3: "idle_3",
    IDLE4: "idle_4",
    IDLE5: "idle_5",
    IDLE6: "idle_6",
    HURT1: "hurt_1",
    HURT2: "hurt_2",
    TANK_BOOSTER_AS: "as/tank_booster_as",
    TANK_BOOSTER_BU: "bu/tank_booster_bu",
    TANK_BOOSTER_BZ: "bz/tank_booster_bz",
    TANK_BOOSTER_GR: "gr/tank_booster_gr"
}

export enum MONSTER_EXPRESSION {
    ANGRY = "angry",
    FLIP_THE_BIRD = "flip_the_bird",
    LAUGH = "laugh",
    SCARED = "scared",
    SHOCK = "shock"
}

export const MULTI_BOX_ANIMATIONS = {
    "LVL_BASE": "lvl_base",
    "LVL_IDLE": "lvl_idle",
    "LVL_TRANSFORM": "lvl_transform",
    "LVL_UPDATE": "lvl_update",
    "LVL_CLOSE": "lvl_close",
    "LVL_1_OPEN": "lvl1_open",
    "LVL_2_OPEN": "lvl2_open",
    "LVL_3_OPEN": "lvl3_open",
    "LVL_4_OPEN": "lvl4_open",
    "OPEN": "open"
}

export const SCATTER_ATTENTION = {
    S2: "2d_attention",
    S3: "3d_attention"
}
